import React, { useState, useEffect } from 'react';

const BunnyImportNew = ({ onImportComplete }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');

  // Load files from backend
  const loadFiles = async () => {
    setLoading(true);
    setImportStatus('Loading files...');
    
    try {
      console.log('🔧 Fetching files from backend...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bunny/files`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Got data:', data);
      
      if (data.audio_files && Array.isArray(data.audio_files)) {
        setFiles(data.audio_files);
        setImportStatus(`Found ${data.audio_files.length} files`);
      } else {
        setFiles([]);
        setImportStatus('No files found');
      }
    } catch (error) {
      console.error('❌ Error loading files:', error);
      setFiles([]);
      setImportStatus('Error loading files: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Select/deselect file
  const toggleFileSelection = (filename) => {
    setSelectedFiles(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  // Select all files
  const selectAll = () => {
    setSelectedFiles(files.map(f => f.filename));
  };

  // Deselect all files
  const deselectAll = () => {
    setSelectedFiles([]);
  };

  // Import selected files
  const importFiles = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to import');
      return;
    }

    setImporting(true);
    setImportStatus('Importing files...');

    try {
      const selectedFileObjects = files.filter(f => selectedFiles.includes(f.filename));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bunny/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          files: selectedFileObjects
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      setImportStatus(`Imported ${result.imported_count || 0} files successfully`);
      setSelectedFiles([]);
      
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('❌ Import error:', error);
      setImportStatus('Import failed: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  // Load files on mount
  useEffect(() => {
    loadFiles();
  }, []);

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#1a1a1a', 
      borderRadius: '8px', 
      color: 'white',
      marginTop: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: '#FFD700' }}>Import from Bunny Storage</h3>
        <div>
          <button 
            onClick={loadFiles} 
            disabled={loading}
            style={{ 
              padding: '8px 16px', 
              marginRight: '8px', 
              backgroundColor: '#444', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            onClick={selectAll} 
            disabled={files.length === 0}
            style={{ 
              padding: '8px 16px', 
              marginRight: '8px', 
              backgroundColor: '#444', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Select All
          </button>
          <button 
            onClick={deselectAll} 
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#444', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Deselect All
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '20px', color: '#ccc' }}>
        Status: {importStatus}
      </div>

      {files.length > 0 ? (
        <>
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto', 
            border: '1px solid #444', 
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {files.map(file => (
              <div 
                key={file.filename}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '12px', 
                  borderBottom: '1px solid #333',
                  backgroundColor: selectedFiles.includes(file.filename) ? '#2a2a2a' : 'transparent'
                }}
              >
                <input 
                  type="checkbox"
                  checked={selectedFiles.includes(file.filename)}
                  onChange={() => toggleFileSelection(file.filename)}
                  style={{ marginRight: '12px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {file.filename}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {file.size_mb} MB
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#ccc' }}>
              {selectedFiles.length} of {files.length} files selected
            </div>
            <button 
              onClick={importFiles}
              disabled={importing || selectedFiles.length === 0}
              style={{ 
                padding: '12px 24px', 
                backgroundColor: '#9333ea', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer',
                opacity: importing || selectedFiles.length === 0 ? 0.5 : 1
              }}
            >
              {importing ? 'Importing...' : `Import ${selectedFiles.length} Selected`}
            </button>
          </div>
        </>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          border: '2px dashed #444',
          borderRadius: '8px'
        }}>
          {loading ? 'Loading files...' : 'No files found'}
        </div>
      )}
    </div>
  );
};

export default BunnyImportNew;